import React, { Component } from "react";
import { connect } from "react-redux";
import { bindActionCreators } from "redux";
import { Link } from "react-router-dom";

import { CardElement, ElementsConsumer } from "@stripe/react-stripe-js";
import { getCurrentUser } from "../actions/user";

import {
  stripeCreateMember,
  stripeConfirmMember,
  stripeGetCustomerInfo,
  updateStripeCustomerInfo,
  stripeGetCouponCode,
  stripeCancelMembership
} from "../actions/membership";

import LoadingCard from "../components/LoadingCard";
import Footer from "../components/Footer";
import BillingSubscriptionInfo from "../components/BillingSubscriptionInfo";
import { BillingListItem } from "../components/BillingListItem";
import gnjLogoMobile from "../assets/images/favicon.svg";

const MOBILE_APP_PLATFORMS = ["android", "ios"];


// A UUID per Subscribe press. crypto.randomUUID needs Safari 15.4+; older browsers build one
// from crypto.getRandomValues.
function newAttemptId() {
  if (window.crypto && typeof window.crypto.randomUUID === 'function') return window.crypto.randomUUID();
  const b = window.crypto.getRandomValues(new Uint8Array(16));
  b[6] = (b[6] & 0x0f) | 0x40;
  b[8] = (b[8] & 0x3f) | 0x80;
  const h = Array.from(b, (x) => x.toString(16).padStart(2, '0')).join('');
  return `${h.slice(0, 8)}-${h.slice(8, 12)}-${h.slice(12, 16)}-${h.slice(16, 20)}-${h.slice(20)}`;
}

export class Billing extends Component {
  constructor(props) {
    super(props);
    this.state = {
      error: "",
      isLoading: false,
      stripeData: {},
      showUpdateCreditCardValue: false,
      couponMsg: "",
      couponDiscount: "",
      couponCode: "",
      isCouponLoading: false,
      membershipPlanSelected: "annual",
      isMobileApp: false,
    };
  }

  _setIsMobileApp = () => {
    if (MOBILE_APP_PLATFORMS.includes(window.__GNJ_MOBILE_PLATFORM__)) {
      this.setState({ isMobileApp: true });
    }
  }

  async componentDidMount() {
    if (!this.props.user.currentUser) {
      await this.props.getCurrentUser();
    }

    const stripeCustomerId = this.props.user.currentUser.attributes["custom:stripeCustomerId"];

    if (stripeCustomerId) {
      try {
        const stripeData = await this.props.stripeGetCustomerInfo(
          stripeCustomerId
        );
        if(stripeData.raw){
          window.confirm(stripeData.raw.message + " Please reload the page.");
        } else {
          this.setState({ stripeData });
        }
      } catch (err) {
        console.log(err);
      }
    }

    this._setIsMobileApp();
  }

  _applyCouponcode = async (couponCodePassedForSubmit) => {
    this.setState({ isCouponLoading: true })
    const couponInfo = await this.props.stripeGetCouponCode(couponCodePassedForSubmit)

    if(couponInfo && couponInfo.id){
      this.setState({
        couponCode: couponInfo.id,
        couponMsg: couponInfo.name + " applied successfully!",
        couponDiscount: couponInfo.percent_off,
        isCouponLoading: false
      })
    } else {
      this.setState({
        couponMsg: couponInfo.error,
        couponDiscount: "",
        couponCode: "",
        isCouponLoading: false
      })
    }
  }

  _showUpdateCreditCard = async e => {
    this.setState({
      showUpdateCreditCardValue: !this.state.showUpdateCreditCardValue
    });
  };

  _creditCardSubmit = async e => {
    e.preventDefault();
    const { stripe, elements } = this.props
    if (!stripe || !elements) {
      return this.setState({ error: "Payment form is not ready.", isLoading: false })
    }

    // The server takes the caller, email and Stripe customer from the sign-in (security sprint
    // PRs 3 and C): only the card, plan, coupon and attempt are sent.
    const { couponCode, membershipPlanSelected } = this.state
    // One ID per press: retries of this attempt can't charge twice; a new press (e.g. a
    // different card after a decline) is a new attempt.
    const attemptId = newAttemptId();
    this.setState({ error: "", isLoading: true });

    const cardElement = elements.getElement(CardElement)
    const token = await stripe.createToken(cardElement);
    if (token.error) {
      return this.setState({
        error: token.error.message,
        isLoading: false
      });
    } else {
      let userSubscribe = await this.props.stripeCreateMember({
        attemptId: attemptId,
        stripeToken: token.token.id,
        stripeCouponId: couponCode,
        membershipPlanSelected: membershipPlanSelected
      });
      if (userSubscribe && userSubscribe.requiresAction) {
        userSubscribe = await this._authenticatePayment(userSubscribe, attemptId);
      }
      if (userSubscribe && userSubscribe.subscription) {
        await this.props.getCurrentUser();
        this.setState({
          error: "",
          isLoading: false,
          showUpdateCreditCardValue: false
        });
        // TODO: maybe use stripeCreateMember function's return data instead of call Stripe again
        const stripeData = await this.props.stripeGetCustomerInfo(userSubscribe.customer.id);
        if(stripeData.raw){
          window.confirm(stripeData.raw.message + " Please reload the page.");
        } else {
          this.setState({ stripeData });
        }
      } else {
        const message = (userSubscribe && (userSubscribe.error || (userSubscribe.raw && userSubscribe.raw.message)))
          || "Something went wrong. Please try again.";
        this.setState({ error: message, isLoading: false });
        // A membership written meanwhile (e.g. by the payment webhook) shows without a reload.
        await this.props.getCurrentUser();
        // Its customer too, or the subscription card waits on stripeData forever.
        const customerId = this.props.user.currentUser?.attributes?.["custom:stripeCustomerId"];
        if (customerId) {
          const stripeData = await this.props.stripeGetCustomerInfo(customerId);
          if (stripeData && !stripeData.raw) this.setState({ stripeData });
        }
      }
    }
  };

  // The card needs 3-D Secure: Stripe.js shows the bank's challenge in a modal iframe on this
  // page (no redirect), then the server finishes the attempt. It is called whatever the
  // challenge's outcome: on success it writes the membership once the subscription is active;
  // on failure or abandon it cancels the waiting subscription, so a retry can't charge twice.
  _authenticatePayment = async ({ clientSecret, subscriptionId }, attemptId) => {
    let authError = null;
    try {
      const result = await this.props.stripe.confirmCardPayment(clientSecret);
      authError = result && result.error;
    } catch (err) {
      authError = err || {};
    }
    const confirmed = await this.props.stripeConfirmMember({ subscriptionId, attemptId });
    if (authError && !(confirmed && confirmed.subscription)) {
      const serverError = confirmed && confirmed.error;
      // The server saw the payment go through (e.g. the challenge passed but Stripe.js lost the
      // response): its message wins, so the user doesn't pay again.
      if (serverError && /payment went through/i.test(serverError)) return { error: serverError };
      // Otherwise Stripe's own reason (e.g. authentication failed) reads better than the server's.
      return { error: authError.message || serverError };
    }
    return confirmed;
  };

  _creaditCardUpdate = async e => {
    e.preventDefault();
    const { stripe, elements } = this.props
    if (!stripe || !elements) {
      return this.setState({ error: "Payment form is not ready.", isLoading: false })
    }

    const { stripeData } = this.state;
    const stripeCustomerId = this.props.user.currentUser.attributes["custom:stripeCustomerId"];
    this.setState({ error: "", isLoading: true });
    const cardElement = elements.getElement(CardElement)
    const token = await stripe.createToken(cardElement);
    if (token.error) {
      return this.setState({
        error: token.error.message,
        isLoading: false
      });
    } else {
      const updatedCreditCard = await this.props.updateStripeCustomerInfo({
        stripeCustomerId: stripeCustomerId, 
        stripeToken: token.token.id 
      });
      if(updatedCreditCard.raw){
        window.confirm(stripeData.raw.message + " Please try again.");
      } else {
        this.setState({
          stripeData: {
            customer: updatedCreditCard,
            charges: stripeData.charges
          },
          error: "",
          isLoading: false,
          showUpdateCreditCardValue: false
        });
      }
    }
  };

  _reactivateMembershipRequest = async () => {
    const { currentUser } = this.props.user
    this.setState({ error: "", isLoading: true });
    const reactivateMembership  = await this.props.stripeCancelMembership(
      currentUser.attributes["custom:stripeSubscriptionId"],
      false
    )
    
    if(reactivateMembership.subscription){
      await this.props.getCurrentUser();
      this.setState({
        error: "",
        isLoading: false
      });
      // TODO: maybe use stripeCancelMembership function's return data instead of call Stripe again
      const stripeData = await this.props.stripeGetCustomerInfo(
        this.props.user.currentUser.attributes["custom:stripeCustomerId"]
      );
      if(stripeData.raw){
        window.confirm(stripeData.raw.message + " Please reload the page.");
      } else {
        this.setState({ stripeData });
      }
    } else {
      this.setState({ error: "Error occurred. Please try again.", isLoading: false });
    }
  }

  _selectedMembershipPlan = async (plan) => {
    this.setState({ membershipPlanSelected: plan });
  }

  render() {
    const {
      error,
      isLoading,
      stripeData,
      showUpdateCreditCardValue,
      couponMsg,
      couponDiscount,
      isCouponLoading,
      isMobileApp
    } = this.state;

    const { currentUser } = this.props.user;
    
    if (!currentUser) return <div className="Billing Container Container--md Container--side-padding"><LoadingCard /></div>;

    const stripeCustomerId = currentUser.attributes["custom:stripeCustomerId"];

    const membership = currentUser.attributes["custom:membership"];
    const billingList = stripeData.charges ? (
      // Only show successful payment history
      stripeData.charges.data.filter((charge) => charge.status === "succeeded").map(billing => (
        <BillingListItem key={billing.id} billingList={billing} />
      ))
    ) : (
      <LoadingCard />
    );

    const mobileAppBillingContent = (
      <div className="Billing Container Container--md Container--side-padding">
        <h1>Subscribe on the Web</h1>
        <div className="journal-list-month gray-bg sm-form">
          <img src={gnjLogoMobile} className="mobile-billing-icon" alt="Goodnight Jornal logo" />
          <p>
            <strong>Subscribing and managing your Goodnight Journal membership isn't available in the mobile app.</strong>
          </p>
          <p>
            To become a member or manage your existing subscription, please visit <strong>goodnightjournal.com</strong> in a web browser on your phone, tablet, or computer.
          </p>
          <p>
            <small>If you're already a member, your subscription and journals are available right here in the app — just log in.</small>
          </p>
        </div>

        <div>
          <Footer />
        </div>
      </div>
    );

    if(isMobileApp) {
      return mobileAppBillingContent;
    }
    
    return (
      <div className="Billing Container Container--md Container--side-padding">
        <h1>Billing</h1>
        <div>
          <h3>My membership</h3>
          <BillingSubscriptionInfo
            stripeCustomer={stripeData}
            stripeCustomerId={stripeCustomerId}
            membership={membership}
            isLoading={isLoading}
            error={error}
            creditCardSubmit={this._creditCardSubmit}
            creaditCardUpdate={this._creaditCardUpdate}
            showUpdateCreditCard={this._showUpdateCreditCard}
            showUpdateCreditCardValue={showUpdateCreditCardValue}
            applyCouponCode={this._applyCouponcode}
            couponMsg={couponMsg}
            couponDiscount={couponDiscount}
            isCouponLoading={isCouponLoading}
            reactivateMembership={this._reactivateMembershipRequest}
            selectedMembershipPlan={this._selectedMembershipPlan}
          />
        </div>

        {stripeCustomerId && 
        <div className="user-stats billing-list">
          <h3>Billing history</h3>
          {billingList}
        </div>}


        {(membership === "annual" || membership === "monthly") && 
          <div>
            <h3>Cancel membership</h3>
            <div>
              <p>
                By cancelling membership, you will lose access to all member's only features at the end of your current membership cycle.
              </p>
              <Link to="/my-account/cancel/" className="cancel-text" id="billing_cancel_membership_button">
                Cancel my membership
              </Link>
            </div>
          </div>
        }

        <div>
          <Footer />
        </div>
      </div>
    );
  }
}

function mapStateToProps(state) {
  return state;
}

function mapDispatchToProps(dispatch) {
  return bindActionCreators(
    {
      stripeCreateMember,
      stripeConfirmMember,
      getCurrentUser,
      stripeGetCustomerInfo,
      updateStripeCustomerInfo,
      stripeGetCouponCode,
      stripeCancelMembership
    },
    dispatch
  );
}

const BillingConnected = connect(
  mapStateToProps,
  mapDispatchToProps
)(Billing);

const BillingWithStripe = (props) => (
  <ElementsConsumer>
    {({ stripe, elements }) => (
      <BillingConnected {...props} stripe={stripe} elements={elements} />
    )}
  </ElementsConsumer>
)

export default BillingWithStripe;
